"use client";

import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { fetchProfile, updateProfile } from "../../features/profile/ProfileServices";
import imageCompression from "browser-image-compression";

import PageLayout from "../../components/layouts/PageLayout";
import CustomNavbar from "../../components/common/CustomNavbar";
import { cameraIcon, profileIconNew, whitePen } from "../../asset";
import { UserProfile } from "../../types/profileType";
import ECSLoader from "../../components/common/EcsLoader";
import { toast } from "sonner";
import { uploadFile } from "../../util/imageUpload";

export default function Profile() {
  const dispatch = useDispatch<AppDispatch>();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState<UserProfile | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [editingPersonal, setEditingPersonal] = useState<boolean>(false);
  const [editingBusiness, setEditingBusiness] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [isUploadingPic, setIsUploadingPic] = useState(false);
  const [picChanged, setPicChanged] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const result = await dispatch(fetchProfile()).unwrap();
        setProfile(result);
        setFormData(result);
        setProfilePicUrl(result.profilePic ?? null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [dispatch]);

  const handleChange = <K extends keyof UserProfile>(
    key: K,
    value: UserProfile[K]
  ) => {
    setFormData((prev) =>
      prev ? { ...prev, [key]: value } : prev
    );
  };

  const handlePicChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    let fileToUpload = file;

    if (file.size > 4 * 1024 * 1024) {
      const options = {
        maxSizeMB: 2.9,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: file.type,
        onProgress: () => { },
      };
      try {
        fileToUpload = await imageCompression(file, options);
      } catch (err) {
        toast.error("Image compression failed.");
        return;
      }
    }

    const formData = new FormData();
    formData.append("file", fileToUpload);
    formData.append("folder", "profile_pics");

    try {
      setIsUploadingPic(true);
      const uploadedUrl = await uploadFile(fileToUpload);
      setProfilePicUrl(uploadedUrl);
      setPicChanged(true);
      toast.success("Profile picture updated!");
    } catch (err: any) {
      toast.error(err.message || "Failed to upload image");
    } finally {
      setIsUploadingPic(false);
    }
  };

  const handleSave = async () => {
    if (!formData) return;

    try {
      const updated = await dispatch(
        updateProfile({
          fullName: formData.fullName,
          mobile: formData.mobile,
          email: formData.email,
          location: formData.location,
          shareLinks: formData.shareLinks,
          designation: formData.designation,
          role: formData.role,
          brandName: formData.brandName,
          address: formData.address,
          outlets: formData.outlets,
          footFall: formData.footFall,
          gst: formData.gst,
          pan: formData.pan,
          profilePic: profilePicUrl ?? "",
          website: formData.website,
        })
      ).unwrap();

      setProfile(updated);
      setFormData(updated);
      setProfilePicUrl(updated.profilePic ?? null);
      setEditingPersonal(false);
      setEditingBusiness(false);
      setPicChanged(false);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <PageLayout
      customNavbar={
        <CustomNavbar>
          <div className="flex gap-3 items-center w-max">
            <span className="font-bold text-lg uppercase">
              profile information
            </span>
          </div>
        </CustomNavbar>
      }
    >
      <div className="flex flex-col gap-3 rounded-4xl w-full bg-gray-100 relative">
        <ECSLoader show={loading} />

        {/* Hidden file input */}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          className="hidden"
          onChange={handlePicChange}
        />

        {/* Top Profile Card */}
        <div className="rounded-4xl w-full flex gap-10 p-7 bg-white items-center">
          <div className="relative w-32 h-32">
            <img
              src={profilePicUrl || profileIconNew}
              alt="Profile"
              className="w-32 h-32 rounded-full object-cover"
            />
            <div
              className="absolute bottom-0 right-0 cursor-pointer"
              onClick={() => !isUploadingPic && fileInputRef.current?.click()}
            >
              {isUploadingPic ? (
                <div className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center">
                  <svg className="animate-spin w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                </div>
              ) : (
                <img
                  src={cameraIcon}
                  alt="camera"
                  className="w-8 h-8 bg-white p-1 rounded-full cursor-pointer shadow-md"
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-lg font-bold text-text-main">
              {formData?.fullName ?? profile?.fullName}
            </span>
            <span className="text-sm font-semibold">
              {formData?.designation ?? profile?.designation ?? "-"}
            </span>
            <span className="text-sm font-semibold">
              {formData?.role ?? profile?.role ?? "-"}
            </span>
          </div>

          {picChanged && (
            <div className="ml-auto">
              <button
                onClick={handleSave}
                className="bg-brand-orange-gradient flex items-center gap-2 px-5 py-3 rounded-full text-white text-sm font-medium"
              >
                <img src={whitePen} alt="Save" className="w-4 h-4" />
                Save Photo
              </button>
            </div>
          )}
        </div>

        {/* Personal Info */}
        <Section
          title="Personal Information"
          editing={editingPersonal}
          onToggle={() =>
            editingPersonal ? handleSave() : setEditingPersonal(true)
          }
        >
          <EditableInfo
            label="Full Name"
            value={formData?.fullName}
            editing={editingPersonal}
            onChange={(v) => handleChange("fullName", v)}
          />
          <EditableInfo
            label="Mobile Number"
            value={formData?.mobile}
            editing={editingPersonal}
            onChange={(v) => handleChange("mobile", v)}
          />
          <EditableInfo
            label="Email Address"
            value={formData?.email}
            editing={editingPersonal}
            onChange={(v) => handleChange("email", v)}
          />
          <EditableInfo
            label="Location"
            value={formData?.location}
            editing={editingPersonal}
            onChange={(v) => handleChange("location", v)}
          />
          <EditableInfo
            label="Designation"
            value={formData?.designation}
            editing={editingPersonal}
            onChange={(v) => handleChange("designation", v)}
          />
          <EditableInfo
            label="Role"
            value={formData?.role}
            editing={editingPersonal}
            editable={false}
            onChange={() => { }}
          />
          <EditableInfo
            label="Share Links"
            value={formData?.shareLinks}
            editing={editingPersonal}
            blue
            onChange={(v) => handleChange("shareLinks", v)}
          />
        </Section>

        {/* Business Info */}
        <Section
          title="Business Information"
          editing={editingBusiness}
          onToggle={() =>
            editingBusiness ? handleSave() : setEditingBusiness(true)
          }
        >
          <EditableInfo
            label="Brand Name"
            value={formData?.brandName}
            editing={editingBusiness}
            onChange={(v) => handleChange("brandName", v)}
          />
          <EditableInfo
            label="Address"
            value={formData?.address}
            editing={editingBusiness}
            onChange={(v) => handleChange("address", v)}
          />
          <EditableInfo
            label="Outlets"
            value={formData?.outlets}
            editing={editingBusiness}
            onChange={(v) => handleChange("outlets", v)}
          />
          <EditableInfo
            label="footFalls"
            value={formData?.footFall}
            editing={editingBusiness}
            onChange={(v) => handleChange("footFall", v)}
          />
          <EditableInfo
            label="GSTIN"
            value={formData?.gst}
            editing={editingBusiness}
            onChange={(v) => handleChange("gst", v)}
          />
          <EditableInfo
            label="PAN"
            value={formData?.pan}
            editing={editingBusiness}
            onChange={(v) => handleChange("pan", v)}
          />
          <EditableInfo
            label="Website"
            value={formData?.website}
            editing={editingBusiness}
            blue
            onChange={(v) => handleChange("website", v)}
          />
        </Section>
      </div>
    </PageLayout>
  );
}

/* ---------------- SECTION WRAPPER ---------------- */

interface SectionProps {
  title: string;
  editing: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function Section({ title, editing, onToggle, children }: SectionProps) {
  return (
    <div className="rounded-4xl w-full p-7 bg-white flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <span className="font-semibold text-lg text-black">{title}</span>
        <div className="flex items-center gap-3">
          <button
            onClick={onToggle}
            className="bg-brand-orange-gradient flex items-center gap-2 px-5 py-3 rounded-full text-white text-sm font-medium"
          >
            <img src={whitePen} alt="Edit" className="w-4 h-4" />
            {editing ? "Save" : "Edit"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 text-sm">
        {children}
      </div>
    </div>
  );
}

/* ---------------- EDITABLE INFO ---------------- */

interface EditableInfoProps {
  label: string;
  value?: string | number;
  editing: boolean;
  blue?: boolean;
  editable?: boolean;
  onChange: (value: string) => void;
}

function EditableInfo({
  label,
  value,
  editing,
  onChange,
  blue,
  editable = true,
}: EditableInfoProps) {
  return (
    <div className="flex flex-col">
      <span className="font-medium">{label}</span>

      {editing && editable ? (
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="border rounded-md px-2 py-1 text-black"
        />
      ) : (
        <span className={`font-semibold ${blue ? "text-blue-500" : "text-black"}`}>
          {value ?? "-"}
        </span>
      )}
    </div>
  );
}